import Phaser from "phaser";
import { trackUiClick, type AnalyticsSource } from "../analytics.js";
import { expandCatLines, type CatLineRender, type CatTextSegment } from "../render/cat-format.js";
import { type LsDisplay } from "../shell/coreutils.js";
import {
    DEFAULT_TELETYPE_ANIMATION,
    OutputBuffer,
    type TextStyle,
} from "../render/output-buffer.js";
import type { VfsSession } from "../shell/vfs.js";
import {
    BANNER_FONT,
    COLORS,
    FONT,
    FONT_SIZE,
    FONT_SIZE_BANNER,
    LINE_H,
    PADDING,
} from "./constants.js";

export interface TerminalOutputCallbacks {
    playClick: () => void;
    runCommand: (command: string, source: AnalyticsSource) => void;
}

export class TerminalOutputView {
    nextLineY = PADDING;
    scrollYOffset = 0;
    scrollPinBottom = true;
    readonly buffer: OutputBuffer;

    private measureProbe?: Phaser.GameObjects.Text;

    constructor(
        private readonly scene: Phaser.Scene,
        private readonly output: Phaser.GameObjects.Container,
        private readonly callbacks: TerminalOutputCallbacks,
        private readonly getScaleWidth: () => number,
        private readonly getPromptTop: () => number,
    ) {
        this.buffer = new OutputBuffer(
            {
                wrapLine: (text, style) => this.wrapTextToLines(text, style),
                createLine: (y, style) => {
                    const line = this.paintLineAt(y, "", style);
                    return {
                        setText: (text) => line.setText(text),
                        getHeight: () => line.height,
                    };
                },
                advanceLine: (lineY, lineHeight) => {
                    this.nextLineY = lineY + Math.max(LINE_H, lineHeight + 8);
                },
                onScroll: () => {
                    this.scrollPinBottom = true;
                    this.scrollOutput();
                },
                schedule: (delayMs, fn) => this.scene.time.delayedCall(delayMs, fn),
            },
            DEFAULT_TELETYPE_ANIMATION,
            () => this.nextLineY,
        );
    }

    outputMaxWidth(): number {
        return Math.max(0, this.getScaleWidth() - PADDING * 2);
    }

    outputMaxChars(): number {
        const charWidth = this.measureTextWidth("M");
        if (charWidth <= 0) {
            return 80;
        }
        return Math.max(24, Math.floor(this.outputMaxWidth() / charWidth));
    }

    clear(): void {
        this.buffer.reset();
        this.output.removeAll(true);
        this.nextLineY = PADDING;
        this.scrollYOffset = 0;
        this.scrollPinBottom = true;
        this.output.y = 0;
    }

    scrollOutput(): void {
        const max = this.maxScroll();
        if (this.scrollPinBottom) {
            this.scrollYOffset = max;
        } else {
            this.scrollYOffset = Phaser.Math.Clamp(this.scrollYOffset, 0, max);
        }
        this.output.y = -this.scrollYOffset;
    }

    onWheel(deltaY: number): void {
        const max = this.maxScroll();
        if (max <= 0) return;

        const step = Phaser.Math.Clamp(Math.abs(deltaY), 40, 160);
        this.scrollPinBottom = false;

        if (deltaY > 0) {
            this.scrollYOffset = Math.min(max, this.scrollYOffset + step);
        } else {
            this.scrollYOffset = Math.max(0, this.scrollYOffset - step);
        }

        if (this.scrollYOffset >= max - 2) {
            this.scrollYOffset = max;
            this.scrollPinBottom = true;
        }

        this.output.y = -this.scrollYOffset;
    }

    appendBanner(banner: string): void {
        this.paintBanner(banner);
    }

    appendLine(text: string, style: TextStyle = {}, instant = false): void {
        if (instant) {
            this.paintLine(text, style);
            return;
        }
        this.buffer.feedWrapped(text, style);
    }

    appendLines(lines: string[], style: TextStyle = {}, instant = false): void {
        for (const text of lines) {
            this.appendLine(text, style, instant);
        }
    }

    appendCatLines(lines: string[]): void {
        const renders = expandCatLines(lines, { maxChars: this.outputMaxChars() });
        let compactBatch: CatLineRender[] = [];

        const flushCompactBatch = () => {
            if (compactBatch.length === 0) {
                return;
            }

            const batch = compactBatch;
            compactBatch = [];
            this.buffer.feedRun(() => {
                for (const render of batch) {
                    this.paintCompactCatLine(render);
                }
                this.scrollOutput();
            });
        };

        for (const render of renders) {
            if (render.compact) {
                compactBatch.push(render);
                continue;
            }

            flushCompactBatch();
            this.appendCatLineRender(render);
        }

        flushCompactBatch();
    }

    appendLsDisplay(display: LsDisplay, vfs: VfsSession): void {
        if (display.type === "error") {
            this.appendLines(display.lines, { color: COLORS.dim });
            return;
        }

        for (const row of display.rows) {
            if (row.type === "short") {
                this.buffer.feedRun(() => this.paintLsShortRow(row.names, vfs));
            } else {
                this.buffer.feedRun(() => this.paintLsLongRow(row.text, row.name, vfs));
            }
        }
    }

    bootStyle(name?: "text" | "dim" | "bright" | "amber"): TextStyle {
        switch (name) {
            case "dim":
                return { color: COLORS.dim };
            case "bright":
                return { color: COLORS.bright };
            case "amber":
                return { color: COLORS.amber };
            default:
                return { color: COLORS.text };
        }
    }

    private maxScroll(): number {
        const viewBottom = this.getPromptTop() - LINE_H;
        return Math.max(0, this.nextLineY - viewBottom);
    }

    private terminalCharMetrics(): { charWidth: number; lineHeight: number } {
        if (!this.measureProbe) {
            this.measureProbe = this.scene.add.text(-10000, -10000, "", {
                fontFamily: FONT,
                fontSize: FONT_SIZE,
            });
            this.measureProbe.setVisible(false);
        }

        this.measureProbe.setFontFamily(FONT);
        this.measureProbe.setFontSize(FONT_SIZE);
        this.measureProbe.setText("M");
        return {
            charWidth: this.measureProbe.width,
            lineHeight: this.measureProbe.height,
        };
    }

    private paintCharGridLine(y: number, text: string, color: string): number {
        const { charWidth, lineHeight } = this.terminalCharMetrics();

        for (let index = 0; index < text.length; index++) {
            const glyph = this.scene.add.text(PADDING + index * charWidth, y, text[index]!, {
                fontFamily: FONT,
                fontSize: FONT_SIZE,
                color,
            });
            this.output.add(glyph);
        }

        return y + lineHeight + 1;
    }

    private measureTextWidth(
        text: string,
        color: string = COLORS.text,
        fontSize = FONT_SIZE,
        fontFamily = FONT,
    ): number {
        if (!this.measureProbe) {
            this.measureProbe = this.scene.add.text(-10000, -10000, "", {
                fontFamily: FONT,
                fontSize: FONT_SIZE,
            });
            this.measureProbe.setVisible(false);
        }

        this.measureProbe.setFontFamily(fontFamily);
        this.measureProbe.setFontSize(fontSize);
        this.measureProbe.setColor(color);
        this.measureProbe.setText(text);
        return this.measureProbe.width;
    }

    private measureBannerMetrics(fontSize: number): { charWidth: number; lineHeight: number } {
        if (!this.measureProbe) {
            this.measureProbe = this.scene.add.text(-10000, -10000, "", {
                fontFamily: FONT,
                fontSize: FONT_SIZE,
            });
            this.measureProbe.setVisible(false);
        }

        this.measureProbe.setFontFamily(BANNER_FONT);
        this.measureProbe.setFontSize(fontSize);
        this.measureProbe.setColor(COLORS.bright);
        this.measureProbe.setText("█");
        return {
            charWidth: this.measureProbe.width,
            lineHeight: this.measureProbe.height,
        };
    }

    private paintBanner(banner: string): void {
        const lines = banner.split("\n");
        const maxWidth = this.outputMaxWidth();
        let fontSize = FONT_SIZE_BANNER;
        const widestChars = Math.max(...lines.map((line) => line.length), 0);

        let { charWidth, lineHeight } = this.measureBannerMetrics(fontSize);
        let linePixelWidth = widestChars * charWidth;

        if (linePixelWidth > maxWidth && linePixelWidth > 0) {
            fontSize = Math.max(10, Math.floor(fontSize * (maxWidth / linePixelWidth)));
            ({ charWidth, lineHeight } = this.measureBannerMetrics(fontSize));
        }

        let y = this.nextLineY;

        for (const line of lines) {
            for (let index = 0; index < line.length; index += 1) {
                const char = line[index]!;
                if (char === " ") continue;

                const glyph = this.scene.add.text(PADDING + index * charWidth, y, char, {
                    fontFamily: BANNER_FONT,
                    fontSize,
                    color: COLORS.bright,
                });
                this.output.add(glyph);
            }

            y += lineHeight + 2;
        }

        this.nextLineY = y + 8;
        this.scrollOutput();
    }

    private splitLongToken(token: string, color: string): string[] {
        const maxWidth = this.outputMaxWidth();
        if (this.measureTextWidth(token, color) <= maxWidth) {
            return [token];
        }

        const parts: string[] = [];
        let chunk = "";

        for (const char of token) {
            const next = chunk + char;
            if (chunk && this.measureTextWidth(next, color) > maxWidth) {
                parts.push(chunk);
                chunk = char;
            } else {
                chunk = next;
            }
        }

        if (chunk) {
            parts.push(chunk);
        }

        return parts;
    }

    private wrapTextToLines(text: string, style: TextStyle = {}): string[] {
        if (!text) return [""];

        const maxWidth = this.outputMaxWidth();
        const color = String(style.color ?? COLORS.text);
        const lines: string[] = [];

        for (const paragraph of text.split("\n")) {
            if (!paragraph) {
                lines.push("");
                continue;
            }

            const tokens = paragraph.match(/\S+|\s+/g) ?? [];
            let current = "";

            for (const token of tokens) {
                const parts = this.splitLongToken(token, color);
                for (const part of parts) {
                    const next = current + part;
                    if (current && this.measureTextWidth(next, color) > maxWidth) {
                        lines.push(current);
                        current = part.trimStart();
                    } else {
                        current = next;
                    }
                }
            }

            if (current) {
                lines.push(current);
            }
        }

        return lines.length > 0 ? lines : [""];
    }

    paintLineAt(y: number, text: string, style: TextStyle = {}, wrap = false): Phaser.GameObjects.Text {
        const line = this.scene.add.text(PADDING, y, text, {
            fontFamily: FONT,
            fontSize: FONT_SIZE,
            color: COLORS.text,
            lineSpacing: 10,
            ...(wrap ? { wordWrap: { width: this.outputMaxWidth() } } : {}),
            ...style,
        });
        this.output.add(line);
        return line;
    }

    private paintLine(text: string, style: TextStyle = {}): Phaser.GameObjects.Text {
        const wrapped = this.wrapTextToLines(text, style);
        let last = this.paintLineAt(this.nextLineY, wrapped[0] ?? "", style);

        for (let index = 1; index < wrapped.length; index += 1) {
            this.nextLineY += Math.max(LINE_H, last.height + 8);
            last = this.paintLineAt(this.nextLineY, wrapped[index]!, style);
        }

        this.nextLineY += Math.max(LINE_H, last.height + 8);
        this.scrollOutput();
        return last;
    }

    private paintLink(label: string, url: string): void {
        const link = this.scene.add
            .text(PADDING, this.nextLineY, label, {
                fontFamily: FONT,
                fontSize: FONT_SIZE,
                color: COLORS.bright,
                wordWrap: { width: this.outputMaxWidth() },
            })
            .setInteractive({ useHandCursor: true })
            .on("pointerover", () => link.setColor(COLORS.amber))
            .on("pointerout", () => link.setColor(COLORS.bright))
            .on("pointerdown", () => {
                this.callbacks.playClick();
                trackUiClick("link", label, url);
                window.open(url, "_blank", "noopener,noreferrer");
            });

        this.output.add(link);
        this.nextLineY += Math.max(LINE_H, link.height + 8);
        this.scrollOutput();
    }

    private paintRichLine(segments: CatTextSegment[]): void {
        const maxWidth = this.outputMaxWidth();
        let y = this.nextLineY;
        let x = PADDING;
        let rowHeight = LINE_H;

        const wrapRow = (): void => {
            y += rowHeight + 8;
            x = PADDING;
            rowHeight = LINE_H;
        };

        const placeToken = (token: string, bright: boolean): void => {
            const color = bright ? COLORS.bright : COLORS.text;
            const parts = this.splitLongToken(token, color);

            for (const part of parts) {
                const width = this.measureTextWidth(part, color);
                if (x > PADDING && x - PADDING + width > maxWidth) {
                    wrapRow();
                }

                const text = this.scene.add.text(x, y, part, {
                    fontFamily: FONT,
                    fontSize: FONT_SIZE,
                    color,
                });
                this.output.add(text);
                x += text.width;
                rowHeight = Math.max(rowHeight, text.height);
            }
        };

        for (const segment of segments) {
            const tokens = segment.text.split(/(\s+)/).filter((token) => token.length > 0);
            for (const token of tokens) {
                placeToken(token, Boolean(segment.bright));
            }
        }

        this.nextLineY = y + rowHeight + 8;
        this.scrollOutput();
    }

    private catRenderStyle(render: CatLineRender): TextStyle {
        switch (render.kind) {
            case "title":
                return { color: COLORS.amber };
            case "dim":
                return { color: COLORS.dim };
            case "bright":
                return { color: COLORS.bright };
            default:
                return {};
        }
    }

    private paintCompactCatLine(render: CatLineRender): void {
        const color = String(this.catRenderStyle(render).color ?? COLORS.text);
        this.nextLineY = this.paintCharGridLine(this.nextLineY, render.text, color);
    }

    private appendCatLineRender(render: CatLineRender): void {
        switch (render.kind) {
            case "plain":
                this.appendLine(render.text);
                return;
            case "title":
                this.appendLine(render.text, { color: COLORS.amber });
                return;
            case "dim":
                this.appendLine(render.text, { color: COLORS.dim });
                return;
            case "bright":
                this.appendLine(render.text, { color: COLORS.bright });
                return;
            case "rich":
                this.buffer.feedRun(() =>
                    this.paintRichLine(render.segments ?? [{ text: render.text }]),
                );
                return;
            case "url":
                this.buffer.feedRun(() =>
                    this.paintLink(render.linkLabel ?? render.text, render.url ?? render.text),
                );
                return;
            case "label-email":
            case "label-url": {
                this.buffer.feedRun(() => {
                    this.paintLine(render.prefix ?? "");
                    this.paintLink(render.linkLabel ?? render.url ?? "", render.url ?? "");
                });
                return;
            }
            case "label-file": {
                this.buffer.feedRun(() => {
                    this.paintLabelFile(
                        render.prefix ?? "",
                        render.linkLabel ?? "",
                        render.command ?? "",
                    );
                });
                return;
            }
        }
    }

    private paintLabelFile(prefix: string, label: string, command: string): void {
        const y = this.nextLineY;
        let x = PADDING;

        if (prefix) {
            const prefixText = this.scene.add.text(x, y, prefix, {
                fontFamily: FONT,
                fontSize: FONT_SIZE,
                color: COLORS.text,
            });
            this.output.add(prefixText);
            x += prefixText.width;
        }

        this.createClickableText(x, y, label, command, {}, COLORS.bright);
        this.nextLineY = y + LINE_H;
        this.scrollOutput();
    }

    private fileOpenCommand(name: string, vfs: VfsSession): string {
        const entry = vfs.lookup(vfs.resolvePath(name));
        if (entry?.type === "file" && entry.downloadSrc) {
            return `wget ${name}`;
        }
        return `cat ${name}`;
    }

    private createClickableText(
        x: number,
        y: number,
        label: string,
        command: string,
        style: TextStyle = {},
        baseColor: string = COLORS.text,
    ): Phaser.GameObjects.Text {
        const item = this.scene.add
            .text(x, y, label, {
                fontFamily: FONT,
                fontSize: FONT_SIZE,
                color: baseColor,
                ...style,
            })
            .setInteractive({ useHandCursor: true })
            .on("pointerover", () => item.setColor(COLORS.amber))
            .on("pointerout", () => item.setColor(baseColor))
            .on("pointerdown", () => {
                this.callbacks.playClick();
                trackUiClick("link", label, command);
                this.callbacks.runCommand(command, "link");
            });

        this.output.add(item);
        return item;
    }

    private paintLsShortRow(names: string[], vfs: VfsSession): void {
        const maxWidth = this.outputMaxWidth();
        let y = this.nextLineY;
        let x = PADDING;
        let rowHeight = LINE_H;

        const wrapRow = (): void => {
            y += rowHeight + 8;
            x = PADDING;
            rowHeight = LINE_H;
        };

        for (let index = 0; index < names.length; index += 1) {
            if (index > 0) {
                const gapWidth = this.measureTextWidth("  ");
                if (x > PADDING && x - PADDING + gapWidth > maxWidth) {
                    wrapRow();
                }

                const gap = this.scene.add.text(x, y, "  ", {
                    fontFamily: FONT,
                    fontSize: FONT_SIZE,
                    color: COLORS.text,
                });
                this.output.add(gap);
                x += gap.width;
                rowHeight = Math.max(rowHeight, gap.height);
            }

            const name = names[index]!;
            const nameWidth = this.measureTextWidth(name);
            if (x > PADDING && x - PADDING + nameWidth > maxWidth) {
                wrapRow();
            }

            const item = this.createClickableText(
                x,
                y,
                name,
                this.fileOpenCommand(name, vfs),
            );
            x += item.width;
            rowHeight = Math.max(rowHeight, item.height);
        }

        this.nextLineY = y + rowHeight + 8;
        this.scrollOutput();
    }

    private paintLsLongRow(text: string, name: string | undefined, vfs: VfsSession): void {
        if (!name || this.measureTextWidth(text) > this.outputMaxWidth()) {
            this.paintLine(text);
            return;
        }

        const y = this.nextLineY;
        const prefix = text.slice(0, text.length - name.length);
        let x = PADDING;

        const prefixText = this.scene.add.text(x, y, prefix, {
            fontFamily: FONT,
            fontSize: FONT_SIZE,
            color: COLORS.text,
        });
        this.output.add(prefixText);
        x += prefixText.width;

        this.createClickableText(x, y, name, this.fileOpenCommand(name, vfs));
        this.nextLineY += LINE_H;
        this.scrollOutput();
    }
}
